/**
 * AP-36 7.2 / 7.4 — das **Register** aller Speicherstellen, außerhalb der
 * Komponenten. Deshalb läuft eine Anfrage, die beim Seitenwechsel noch unterwegs
 * ist, zu Ende, und ihr Fehler erreicht den Benutzer auch dann noch.
 *
 * Es hängt dreierlei an `window`:
 * - `beforeunload`: warnt, solange etwas offen ist (`Speicherstelle.mussWarnen`);
 * - `pagehide`: sendet geänderte, noch fokussierte Felder mit `keepalive`;
 * - `keydown` in der Capture-Phase am `window`: Esc gehört zuerst einem Feld mit
 *   ungesendeter Änderung (3.2, S-18). Das `Modal` hört am `document` — das
 *   `window` kommt vorher.
 */
import { api, getAccessToken } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige, hinweis } from '@/lib/dialoge'
import { Speicherstelle, type StelleOptionen, type Umgebung } from './stelle'

const stellen = new Map<string, Speicherstelle<unknown>>()

/** Felder mit ungesendeter Eingabe: Element → „zurücksetzen" (gibt `true`, wenn es etwas zurückzusetzen gab). */
const escZiele = new Map<Element, () => boolean>()

let umgebung: Umgebung = {
  frage: (text, ok) => bestaetige(text, { ok }),
  fehlerText: (e) => {
    // Fachliche Prüfungen liefern eine Liste konkreter Meldungen („Regel 2: Trennzeichen fehlt.") — ungekürzt zeigen.
    const liste = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message
    if (Array.isArray(liste) && liste.length > 0) return liste.map(String).join(' ')
    return apiErrorText(e, 'Das hat nicht geklappt.')
  },
  hinweis: (text) => void hinweis(text),
}

/** Nur für Tests. */
export function setzeUmgebung(neu: Partial<Umgebung>) {
  umgebung = { ...umgebung, ...neu }
}

let angehaengt = false

function sendeKeepalive(url: string, method: string, body: string) {
  const token = getAccessToken()
  if (!token) return
  try {
    void fetch(`${api.defaults.baseURL}${url}`, {
      method,
      keepalive: true,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body,
    })
  } catch {
    // Der Browser hat abgelehnt — die Warnung beim Schließen kam vorher.
  }
}

function haengeAn() {
  if (angehaengt || typeof window === 'undefined') return
  angehaengt = true
  window.addEventListener('beforeunload', (e) => {
    if (!speicherRegister.mussWarnen()) return
    e.preventDefault()
    // Ältere Browser brauchen den Rückgabewert.
    e.returnValue = ''
  })
  window.addEventListener('pagehide', () => {
    for (const s of stellen.values()) s.sendeBeimSchliessen(sendeKeepalive)
  })
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape') return
      const ziel = document.activeElement
      const zuruecksetzen = ziel ? escZiele.get(ziel) : undefined
      if (zuruecksetzen?.()) {
        e.preventDefault()
        e.stopImmediatePropagation()
      }
    },
    true,
  )
}

export const speicherRegister = {
  /**
   * Holt die Stelle zum Schlüssel oder legt sie an — **ohne** den Benutzer zu
   * zählen. Gezählt wird mit `melde` im Effekt: StrictMode baut Effekte einmal
   * ab und wieder auf, ein Zählen im Render ginge dabei aus dem Tritt.
   */
  holeOhneZaehlen<A>(opts: StelleOptionen<A>): Speicherstelle<A> {
    haengeAn()
    let s = stellen.get(opts.schluessel) as Speicherstelle<A> | undefined
    if (!s) {
      // Über Weiterleitungen, damit `setzeUmgebung` auch bestehende Stellen erreicht.
      s = new Speicherstelle<A>(opts, {
        frage: (text, ok) => umgebung.frage(text, ok),
        fehlerText: (e) => umgebung.fehlerText(e),
        hinweis: (text) => umgebung.hinweis(text),
      })
      stellen.set(opts.schluessel, s as Speicherstelle<unknown>)
    }
    return s
  },

  /** Ein Benutzer mehr; eine schon abgeräumte Stelle kommt ins Register zurück. */
  melde(stelle: Speicherstelle<unknown>) {
    stelle.benutzer++
    stelle.beiLeer = undefined
    if (!stellen.has(stelle.schluessel)) stellen.set(stelle.schluessel, stelle)
  },

  /** Gibt die Stelle zurück; die letzte Rückgabe räumt ab, sobald nichts mehr läuft. */
  gib(stelle: Speicherstelle<unknown>) {
    stelle.benutzer = Math.max(0, stelle.benutzer - 1)
    if (stelle.benutzer > 0) return
    stelle.pruefFehler.clear()
    const abraeumen = () => {
      if (stelle.benutzer === 0 && stelle.offen === 0 && stellen.get(stelle.schluessel) === stelle) {
        stellen.delete(stelle.schluessel)
      }
    }
    if (stelle.offen === 0) abraeumen()
    else stelle.beiLeer = abraeumen
  },

  /** Rückfrage über die Umgebung (in Tests ersetzbar). */
  frage(text: string, ok: string): Promise<boolean> {
    return umgebung.frage(text, ok)
  },

  mussWarnen(): boolean {
    for (const s of stellen.values()) if (s.mussWarnen()) return true
    return false
  },

  /** Nur für Tests und Fehlersuche. */
  stelle(schluessel: string): Speicherstelle<unknown> | undefined {
    return stellen.get(schluessel)
  },

  meldeEsc(element: Element, zuruecksetzen: () => boolean): () => void {
    haengeAn()
    escZiele.set(element, zuruecksetzen)
    return () => {
      if (escZiele.get(element) === zuruecksetzen) escZiele.delete(element)
    }
  },

  /** Nur für Tests. */
  leere() {
    stellen.clear()
    escZiele.clear()
  },
}
